import { useMemo, type JSX } from 'react';
import { Banknote, MessageCircle, Percent, Receipt, Send, Sparkles, UserCheck, Video, Wallet } from 'lucide-react';
import { Pill } from '@/components/ui/pill';
import { useScorecard } from '@/hooks/useInsights';
import type { ScorecardTeamRow } from '@/api/insights';
import {
  changeOf,
  formatWindow,
  insightsInvitesHref,
  JOINED_STAGES,
  JOINS_DATED_FROM,
  money,
  parseUtcDay,
  POSTED_STAGES,
  REPLIED_STAGES,
  toNum,
} from '@/lib/insights';
import { scorecardStory } from '@/lib/insightsStory';
import { formatDate, formatWeekRange } from '@/lib/dates';
import { useInsightsScope } from './scope';
import {
  CountLink,
  KpiTile,
  LoadError,
  Loading,
  MetricLabel,
  Note,
  Panel,
  RateValue,
  Section,
  Th,
  Unavailable,
  WeekBars,
} from './parts';

/** Local fallbacks, used only when the backend sends no definition for a key. */
const FALLBACK_DEFS: Record<string, string> = {
  messages: '“I messaged them” records first made in this period.',
  replied: 'Invites logged in this period, not rejected or hidden. Expired invites count: they were real replies.',
  replyRate:
    'Replies after a recorded message ÷ messages. Only messages recorded with “I messaged them” count, so teams that don’t record messages have no reply rate.',
  joinedCohort: 'Of the replies logged in this period, how many have joined (or posted) since.',
  joinRate: 'Joined ÷ replies logged in this period, for teams whose joins are tracked.',
  postedActivity: 'First approved posts recorded in this period, whenever the reply was logged.',
  postRate: 'Posted ÷ replies logged in this period, for teams whose posts are tracked.',
  cashSpent: 'Payouts marked paid in this period, including prizes, retainers and top-ups.',
  costPerPost: 'Cash spent ÷ posts in this period.',
  owedNow:
    'What the ledger says is owed to outreachers right now, across every period. Credit isn’t netted against it. Payroll’s owed figure uses an older formula, so the two can differ.',
};

const NOT_TRACKED = 'Joins and posts on this team are updated by hand, so its rates aren’t shown';

export function ScorecardTab(): JSX.Element {
  const scope = useInsightsScope();
  const q = useScorecard(scope.params);
  if (q.isLoading) return <Loading label="Loading the scorecard…" />;
  if (q.isError || !q.data) return <LoadError error={q.error} what="the scorecard" />;

  const d = q.data;
  const def = (key: string): string | undefined => d.definitions?.[key] ?? FALLBACK_DEFS[key];
  const window = scope.windows.current;
  const team = scope.team || undefined;
  const cur = d.current;
  const prev = d.prior;
  const anyTracked = d.byTeam.length === 0 || d.byTeam.some((t) => t.tracked);
  const rateReason = anyTracked ? undefined : NOT_TRACKED;
  const story = scorecardStory({ current: cur, prior: prev, byTeam: d.byTeam });
  const comparedWith = formatWindow(d.previous);

  return (
    <div className="space-y-6">
      <div className="panel flex items-start gap-3 rounded-xl p-4">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-brand/25 bg-brand/10 text-brand">
          <Sparkles className="h-4 w-4" aria-hidden />
        </span>
        <div className="min-w-0">
          <p className="text-[0.9375rem] font-semibold leading-snug" data-testid="insights-story">
            {story}
          </p>
          {comparedWith ? (
            <p className="mt-1 text-xs text-muted-foreground">Compared with {comparedWith}.</p>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiTile
          icon={MessageCircle}
          label="Messages"
          def={def('messages')}
          value={cur.messages.toLocaleString()}
          change={changeOf(cur.messages, prev.messages)}
        />
        <KpiTile
          icon={Send}
          label="Replies"
          def={def('replied')}
          value={cur.replied.toLocaleString()}
          to={cur.replied > 0 ? insightsInvitesHref({ team, stages: REPLIED_STAGES, window }) : null}
          change={changeOf(cur.replied, prev.replied)}
        />
        <KpiTile
          icon={Percent}
          label="Reply rate*"
          def={def('replyRate')}
          value={
            <RateValue
              rate={cur.replyRate}
              n={cur.messages}
              of="messages"
              reason="No messages recorded with “I messaged them” in this period"
            />
          }
          change={changeOf(cur.replyRate, prev.replyRate, { kind: 'rate' })}
          sub={`* ${cur.repliedAfterMessage.toLocaleString()} replies after a recorded message`}
        />
        <KpiTile
          icon={UserCheck}
          label="Joined"
          def={def('joinedCohort')}
          value={cur.joinedCohort.toLocaleString()}
          to={cur.joinedCohort > 0 ? insightsInvitesHref({ team, stages: JOINED_STAGES, window }) : null}
          change={changeOf(cur.joinedCohort, prev.joinedCohort)}
          sub={
            <>
              of this period’s replies · join rate{' '}
              <RateValue rate={cur.joinRate} n={cur.repliedTracked} reason={rateReason} />
            </>
          }
        />
        <KpiTile
          icon={Video}
          label="Posted"
          def={def('postedActivity')}
          value={cur.postedActivity.toLocaleString()}
          to={
            cur.postedActivity > 0 ? insightsInvitesHref({ team, stages: POSTED_STAGES, window, by: 'posted' }) : null
          }
          change={changeOf(cur.postedActivity, prev.postedActivity)}
          sub={
            <>
              first posts in this period · post rate{' '}
              <RateValue rate={cur.postRate} n={cur.repliedTracked} reason={rateReason} />
            </>
          }
        />
        <KpiTile
          icon={Banknote}
          label="Cash spent"
          def={def('cashSpent')}
          value={money(cur.cashSpent)}
          to="/admin/payouts?status=PAID"
          change={changeOf(toNum(cur.cashSpent), toNum(prev.cashSpent), { kind: 'money' })}
          sub={`${money(cur.inviteEarningsPaid)} invite earnings + ${money(cur.additionalPaid)} additional`}
        />
        <KpiTile
          icon={Receipt}
          label="Cost per post"
          def={def('costPerPost')}
          emphasis
          value={cur.costPerPost == null ? <Unavailable reason="No posts in this period to divide by" /> : money(cur.costPerPost)}
          change={changeOf(toNum(cur.costPerPost), toNum(prev.costPerPost), { kind: 'money', lowerIsBetter: true })}
          sub="cash spent ÷ posts in this period"
        />
        <KpiTile
          icon={Wallet}
          label="Owed now (ledger)"
          def={def('owedNow')}
          value={money(cur.owedNow)}
          to={toNum(cur.owedNow) ? insightsInvitesHref({ team, owed: true }) : null}
          sub={`to ${cur.owedOutreachers.toLocaleString()} outreacher${cur.owedOutreachers === 1 ? '' : 's'}, right now${
            (toNum(cur.creditNow) ?? 0) > 0 ? ` · ${money(cur.creditNow)} credit kept apart` : ''
          }`}
        />
      </div>
      {!anyTracked ? <Note>{NOT_TRACKED}.</Note> : null}

      <WeeklyTrend weekly={d.weekly} def={def} />

      <TeamTable rows={d.byTeam} def={def} />
    </div>
  );
}

// ── 12-week trend ───────────────────────────────────────────────────────────

function WeeklyTrend({
  weekly,
  def,
}: {
  weekly: { weekStart: string; messages: number; replied: number; joinedActivity: number; postedActivity: number }[];
  def: (key: string) => string | undefined;
}): JSX.Element {
  const labels = weekly.map((w) => formatWeekRange(w.weekStart));
  const joinsDatedFrom = parseUtcDay(JOINS_DATED_FROM)!;
  const undatedJoinWeeks = weekly.filter((w) => new Date(`${w.weekStart.slice(0, 10)}T00:00:00Z`) < joinsDatedFrom).length;
  const series = [
    { key: 'messages', label: 'Messages', def: def('messages'), values: weekly.map((w) => w.messages) },
    { key: 'replied', label: 'Replies', def: def('replied'), values: weekly.map((w) => w.replied) },
    {
      key: 'joined',
      label: 'Joins',
      def: def('joinedActivity') ?? 'Joins recorded in each week, whenever the reply was logged.',
      values: weekly.map((w) => w.joinedActivity),
    },
    { key: 'posted', label: 'Posts', def: def('postedActivity'), values: weekly.map((w) => w.postedActivity) },
  ];
  const first = weekly[0]?.weekStart;
  const last = weekly[weekly.length - 1]?.weekStart;

  return (
    <Section
      title="Is the pipeline growing week to week?"
      description={
        first && last
          ? `Messages, replies, joins and posts in each of the last ${weekly.length} weeks, from the week of ${weekOf(first)} to the week of ${weekOf(last)}. Each chart has its own scale; the solid bar is the latest week.`
          : undefined
      }
    >
      {weekly.length === 0 ? (
        <Panel>
          <p className="text-sm text-muted-foreground">No weeks to show yet.</p>
        </Panel>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {series.map((s) => {
            const total = s.values.reduce((a, b) => a + b, 0);
            const latest = s.values[s.values.length - 1] ?? 0;
            return (
              <Panel key={s.key} className="min-w-0 p-3">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-[0.8125rem] font-semibold text-muted-foreground">
                    <MetricLabel label={s.label} def={s.def} />
                  </span>
                  <span className="font-mono text-sm tabular-nums" title="Latest week">
                    {latest.toLocaleString()}
                  </span>
                </div>
                <div className="mt-2">
                  <WeekBars name={s.label} values={s.values} labels={labels} />
                </div>
                <p className="mt-1.5 text-xs text-muted-foreground">
                  {total.toLocaleString()} over {weekly.length} weeks
                  {s.key === 'joined' && undatedJoinWeeks > 0
                    ? ` · joins are dated from ${formatDate(`${JOINS_DATED_FROM}T12:00:00Z`)}, so earlier weeks read 0`
                    : ''}
                </p>
              </Panel>
            );
          })}
        </div>
      )}
    </Section>
  );
}

function weekOf(weekStart: string): string {
  return formatDate(`${weekStart.slice(0, 10)}T12:00:00Z`);
}

// ── Per team ────────────────────────────────────────────────────────────────

function TeamTable({ rows, def }: { rows: ScorecardTeamRow[]; def: (key: string) => string | undefined }): JSX.Element {
  const scope = useInsightsScope();
  const window = scope.windows.current;
  const sorted = useMemo(() => [...rows].sort((a, b) => b.postedActivity - a.postedActivity || b.replied - a.replied), [rows]);
  const untracked = rows.filter((r) => !r.tracked);

  return (
    <Section
      title="Which teams turn replies into posts?"
      description="Join and post rates follow this period’s replies forward; posts and cost per post count what happened in the period."
    >
      {sorted.length === 0 ? (
        <Panel>
          <p className="text-sm text-muted-foreground">No team recorded anything in this period.</p>
        </Panel>
      ) : (
        <div className="table-frame relative overflow-x-auto">
          <table className="responsive-stack w-full text-sm" aria-label="By team">
            <thead>
              <tr className="text-left">
                <Th label="Team" numeric={false} />
                <Th label="Messages" def={def('messages')} />
                <Th label="Replies" def={def('replied')} />
                <Th label="Reply rate*" def={def('replyRate')} />
                <Th label="Joined" def={def('joinedCohort')} />
                <Th label="Join rate" def={def('joinRate')} />
                <Th label="Posts" def={def('postedActivity')} />
                <Th label="Post rate" def={def('postRate')} />
                <Th label="Cash spent" def={def('cashSpent')} />
                <Th label="Cost per post" def={def('costPerPost')} />
                <Th label="Owed now (ledger)" def={def('owedNow')} />
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <TeamRow key={r.team} row={r} window={window} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {untracked.length > 0 ? (
        <Note>
          {untracked.map((r) => scope.teamName(r.team, r.teamDisplayName)).join(' and ')}{' '}
          {untracked.length === 1 ? 'isn’t' : 'aren’t'} tracked: joins and posts are updated by hand, so counts show
          but rates don’t, and {untracked.length === 1 ? 'it is' : 'they are'} left out of the overall join and post
          rates.
        </Note>
      ) : null}
    </Section>
  );
}

function TeamRow({ row: r, window }: { row: ScorecardTeamRow; window: { from: Date; to: Date } }): JSX.Element {
  const scope = useInsightsScope();
  const reason = r.tracked ? undefined : NOT_TRACKED;
  const name = scope.teamName(r.team, r.teamDisplayName);
  return (
    <tr className="border-t border-border transition-colors hover:bg-accent/40">
      <td className="px-3 py-2 font-medium">
        <span className="inline-flex flex-wrap items-center gap-1.5">
          {name}
          {!r.tracked ? (
            <Pill tone="muted" icon={false}>
              Not tracked
            </Pill>
          ) : null}
        </span>
      </td>
      <td className="px-3 py-2 text-right font-mono" data-label="Messages">
        {r.messages.toLocaleString()}
      </td>
      <td className="px-3 py-2 text-right" data-label="Replies">
        <CountLink value={r.replied} to={insightsInvitesHref({ team: r.team, stages: REPLIED_STAGES, window })} />
      </td>
      <td className="px-3 py-2 text-right" data-label="Reply rate*">
        <RateValue rate={r.replyRate} n={r.messages} of="messages" reason="No messages recorded for this team" />
      </td>
      <td className="px-3 py-2 text-right" data-label="Joined">
        <CountLink value={r.joinedCohort} to={insightsInvitesHref({ team: r.team, stages: JOINED_STAGES, window })} />
      </td>
      <td className="px-3 py-2 text-right" data-label="Join rate">
        <RateValue rate={r.joinRate} n={r.repliedTracked} reason={reason} />
      </td>
      <td className="px-3 py-2 text-right" data-label="Posts">
        <CountLink
          value={r.postedActivity}
          to={insightsInvitesHref({ team: r.team, stages: POSTED_STAGES, window, by: 'posted' })}
        />
      </td>
      <td className="px-3 py-2 text-right" data-label="Post rate">
        <RateValue rate={r.postRate} n={r.repliedTracked} reason={reason} />
      </td>
      <td className="px-3 py-2 text-right font-mono" data-label="Cash spent">
        {money(r.cashSpent)}
      </td>
      <td className="px-3 py-2 text-right font-mono" data-label="Cost per post">
        {r.costPerPost == null ? <Unavailable reason="No posts in this period" /> : money(r.costPerPost)}
      </td>
      <td className="px-3 py-2 text-right font-mono" data-label="Owed now (ledger)">
        {money(r.owedNow)}
      </td>
    </tr>
  );
}

